import { useState, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { Icon } from './icons'
import { ACCOUNT_NAV, NAV } from '../nav'
import './Shell.css'

function Sidebar() {
  const [menuOpen, setMenuOpen] = useState(false)
  // same queryKey as SummaryPreview, so React Query serves this from cache
  const { data: company } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => api.getCompanyProfile(),
  })

  return (
    <aside className="side">
      <div className="logo">NexExec</div>

      <nav className="nav" aria-label="Main">
        {NAV.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
          >
            <Icon name={item.icon} />
            {item.label}
            {item.badge !== undefined && <span className="nb tnum">{item.badge}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="side-foot">
        <button
          className="side-u"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="usermenu"
          onClick={() => setMenuOpen((o) => !o)}
        >
          {/* Name, initials and role are still literals: the contract has no
              full-name field (DashboardSummary sends only a first name) and no
              role/entitlement resource. Tracked in ne-api/docs/UNBACKED-DATA.md.
              The company name below IS backed. */}
          <span className="side-av" aria-hidden="true">SM</span>
          <span>
            <span className="side-un" style={{ display: 'block' }}>Sarah Mitchell</span>
            <span className="side-um" style={{ display: 'block' }}>{company?.name ?? ' '}</span>
            <span className="side-um" style={{ display: 'block' }}>Admin</span>
          </span>
          <svg className="side-c" width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="m5.5 8 4.5 4.5L14.5 8" />
          </svg>
        </button>

        {menuOpen && (
          <div className="side-menu" id="usermenu">
            {ACCOUNT_NAV.map((item) => (
              <div key={item.path} style={{ display: 'contents' }}>
                {item.rule && <hr />}
                <NavLink to={item.path} onClick={() => setMenuOpen(false)}>
                  <Icon name={item.icon} className="mi" />
                  {item.label}
                </NavLink>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  )
}

const RING: Record<'gold' | 'teal' | 'dark', string> = {
  gold: 'ring r-gold',
  teal: 'ring r-teal',
  dark: 'ring r-dk',
}

function Header() {
  const { data } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => api.getDashboardSummary(),
  })

  return (
    <div className="top">
      <div>
        {/* no fallback name — greeting the wrong person is worse than a blank */}
        <h1 className="hello">{data ? `Welcome back, ${data.greeting_name}` : ' '}</h1>
        <div className="stats">
          {data?.stats.map((stat) => (
            <span className="stat" key={stat.key}>
              <span className={RING[stat.accent]} />
              <b>{stat.value}</b> {stat.label}
            </span>
          ))}
        </div>
      </div>
      <div className="top-a">
        <button className="sq" type="button" aria-label="Notifications">
          <Icon name="bell" className="ni" />
          {!!data?.notification_count && <span className="n tnum">{data.notification_count}</span>}
        </button>
        <button className="sq" type="button" aria-label="Help and support">
          <Icon name="help" className="ni" />
        </button>
      </div>
    </div>
  )
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="app">
      <Sidebar />
      <div className="main">
        <div className="wrap">
          <Header />
          {children}
        </div>
      </div>
    </div>
  )
}
